import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectShare } from "../../views/App/AppSelectors";
import { Dialog, IconButton, makeStyles, useTheme } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { buildCheckoutUrl } from "../../utils/helper";

/* ─── Tokens ────────────────────────────────────────────────────────────── */
const T = {
  brand:    "#22C55E",
  brandDk:  "#16A34A",
  brand50:  "#F0FDF4",
  brand100: "#DCFCE7",
  ink:      "#0F172A",
  sub:      "#64748B",
  border:   "#E2E8F0",
};

const useStyles = makeStyles((theme) => ({
  paper: {
    fontFamily: "'Inter', 'Be Vietnam Pro', sans-serif",
    borderRadius: 16,
    background: "#ffffff",
    position: "relative",
    color: T.ink,
    maxWidth: 1024,
    width: "100%",
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(15,23,42,0.12)",
  },

  /* ── Close ─────────────────────────────────────────────────────────────── */
  closeBtn: {
    position: "absolute",
    right: 12,
    top: 12,
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: "none",
    background: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#94A3B8",
    transition: "background .12s",
    "&:hover": { background: "#F1F5F9", color: "#475569" },
    "& svg": { width: 18, height: 18, stroke: "currentColor", strokeWidth: 2, fill: "none" },
  },

  /* ── Header ─────────────────────────────────────────────────────────────── */
  header: {
    textAlign: "center",
    padding: "28px 28px 6px",
  },
  chipBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: T.brand50,
    color: T.brandDk,
    fontSize: 12,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 999,
    marginBottom: 10,
    "& svg": { width: 13, height: 13, fill: "currentColor", flexShrink: 0 },
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: T.ink,
    margin: "0 0 6px",
    lineHeight: 1.25,
    [theme.breakpoints.down("sm")]: { fontSize: 19 },
  },
  subtitle: {
    fontSize: 13.5,
    color: T.sub,
    margin: 0,
    lineHeight: 1.5,
  },

  /* ── Toggle ─────────────────────────────────────────────────────────────── */
  toggleWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "18px 0 0",
  },
  toggleInner: {
    display: "inline-flex",
    background: "#F1F5F9",
    borderRadius: 999,
    padding: 4,
    gap: 2,
  },
  tabBtn: {
    padding: "7px 20px",
    borderRadius: 999,
    border: "none",
    fontFamily: "inherit",
    fontSize: 13.5,
    fontWeight: 500,
    cursor: "pointer",
    background: "transparent",
    color: T.sub,
    transition: "all .15s",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  tabActive: {
    background: "#fff",
    color: T.ink,
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  },
  lifetimeSaveBadge: {
    fontSize: 10,
    background: "#FEF3C7",
    color: "#92400E",
    fontWeight: 600,
    padding: "2px 6px",
    borderRadius: 999,
  },

  /* ── Grid ───────────────────────────────────────────────────────────────── */
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    padding: "22px 24px",
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  grid2: {
    gridTemplateColumns: "repeat(2, 1fr)",
    maxWidth: 700,
    margin: "0 auto",
    padding: "22px 24px",
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },

  /* ── Card ───────────────────────────────────────────────────────────────── */
  card: {
    border: `1.5px solid ${T.border}`,
    borderRadius: 12,
    padding: "22px 22px",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    transition: "border-color .15s",
    "&:hover": { borderColor: "#CBD5E1" },
    background: "#fff",
  },
  cardPopular: {
    border: `2px solid ${T.brand}`,
    boxShadow: "0 4px 20px rgba(34,197,94,.1)",
    background: "rgba(240,253,244,0.3)",
    transform: "scale(1.03)",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: { transform: "none" },
    "&:hover": { borderColor: T.brand },
  },

  /* popular badge */
  popularBadge: {
    position: "absolute",
    top: -12,
    left: "50%",
    transform: "translateX(-50%)",
    background: T.brand,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 4,
    boxShadow: "0 2px 6px rgba(34,197,94,.3)",
    "& svg": { width: 11, height: 11, fill: "#fff", flexShrink: 0 },
  },

  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 2,
    marginBottom: 14,
  },
  planName: {
    fontSize: 16,
    fontWeight: 600,
    color: T.ink,
  },
  discountChip: {
    fontSize: 11,
    fontWeight: 500,
    color: "#DC2626",
    background: "#FEF2F2",
    padding: "2px 8px",
    borderRadius: 999,
  },

  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 4,
  },
  priceMain: {
    fontSize: 24,
    fontWeight: 700,
    color: T.ink,
    letterSpacing: "-0.5px",
    lineHeight: 1,
  },
  priceOrig: {
    fontSize: 12,
    color: "#94A3B8",
    textDecoration: "line-through",
  },
  priceNote: {
    fontSize: 11.5,
    color: T.sub,
    marginBottom: 14,
  },
  priceNotePopular: { color: T.brandDk, fontWeight: 500 },
  planDesc: {
    fontSize: 12,
    color: T.sub,
    lineHeight: 1.6,
    marginBottom: 16,
    flex: "0 0 auto",
  },

  /* CTA */
  ctaBtn: {
    width: "100%",
    padding: "11px 16px",
    borderRadius: 8,
    border: `1.5px solid ${T.border}`,
    background: "transparent",
    color: T.ink,
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color .12s, background .12s, color .12s",
    marginBottom: 18,
    "&:hover": { borderColor: "#CBD5E1" },
  },
  ctaBtnPopular: {
    background: T.brand,
    border: `1.5px solid ${T.brand}`,
    color: "#fff",
    fontWeight: 600,
    boxShadow: "0 2px 8px rgba(34,197,94,.25)",
    "&:hover": { background: T.brandDk, borderColor: T.brandDk },
  },

  /* Features */
  featureList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    borderTop: `1px solid ${T.border}`,
    paddingTop: 14,
    flex: 1,
  },
  featureListPopular: { borderColor: "rgba(34,197,94,.25)" },
  featItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: T.sub,
    lineHeight: 1.4,
    padding: "2px 0",
    "& svg": { width: 14, height: 14, stroke: T.brand, strokeWidth: 3, fill: "none", flexShrink: 0 },
  },
  featItemPopular: { color: T.ink },
  /* highlighted feature */
  featHl: {
    background: T.brand50,
    color: T.brandDk,
    fontWeight: 600,
    borderRadius: 6,
    padding: "3px 6px",
    margin: "0 -6px",
    "& svg": { stroke: T.brandDk },
  },

  /* ── Upsell strip (lifetime) ─────────────────────────────────────────────── */
  upsellStrip: {
    margin: "0 140px 12px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#F8FAFC",
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: "12px 16px",
    fontSize: 13,
    color: T.ink,
    lineHeight: 1.5,
    "& svg": { width: 18, height: 18, fill: "#F59E0B", flexShrink: 0 },
    "& b": { color: T.brandDk },
  },

  /* ── Social proof ────────────────────────────────────────────────────────── */
  socialProof: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 24,
    borderTop: `1px solid ${T.border}`,
    padding: "12px 24px",
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { gap: 12 },
  },
  spItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    color: T.sub,
    "& svg": { width: 14, height: 14, fill: T.brand, flexShrink: 0 },
    "& b": { color: T.ink },
  },
  stars: { color: "#FBBF24", fontSize: 13, letterSpacing: 1 },
}));

/* ─── Plan data ─────────────────────────────────────────────────────────── */
const pricingData = {
  vi: {
    chipLabel: "Gói hiện tại: Miễn phí",
    title: "Nâng cấp để gửi tin không giới hạn",
    subtitle: "Chọn gói phù hợp — hủy bất cứ lúc nào, không phí ẩn.",
    monthlyTab: "Theo tháng",
    lifetimeTab: "Vĩnh viễn",
    lifetimeSave: "tiết kiệm đến 84%",
    popularBadge: "Được chọn nhiều nhất",
    ctaDefault: "Bắt đầu dùng",
    ctaPopular: "Bắt đầu dùng →",
    upsellText: "Chỉ <b>+1.000K</b> để chuyển từ <b>Vĩnh viễn</b> sang <b>Vô hạn</b> — mở khóa toàn bộ nâng cấp tương lai, miễn phí trọn đời.",
    monthly: [
      {
        id: "2635",
        name: "3 tháng",
        discount: "-23%",
        price: "999K",
        original: "1.299K",
        priceNote: "≈ 333K/tháng",
        desc: "Phù hợp doanh nghiệp muốn thử trước khi cam kết lâu dài.",
        features: [
          { text: "Không giới hạn số tin gửi" },
          { text: "Không giới hạn Fanpage" },
          { text: "Lọc SĐT + gắn tag" },
          { text: "Hỗ trợ trong 72h" },
          { text: "Loại bỏ \"Bombot.vn\" trong tin nhắn", hl: true },
        ],
      },
      {
        id: "4414",
        name: "6 tháng",
        discount: "-21%",
        price: "1.899K",
        original: "2.399K",
        priceNote: "≈ 317K/tháng — rẻ hơn gói 3 tháng",
        desc: "Lựa chọn tối ưu cho doanh nghiệp đang phát triển.",
        popular: true,
        features: [
          { text: "Tất cả tính năng gói 3 tháng" },
          { text: "Ưu tiên xử lý hàng đợi gửi", hl: true },
          { text: "Hỗ trợ trong 72h" },
          { text: "Loại bỏ \"Bombot.vn\" trong tin nhắn" },
        ],
      },
      {
        id: "2636",
        name: "12 tháng",
        discount: "-25%",
        price: "2.999K",
        original: "3.999K",
        priceNote: "≈ 250K/tháng — rẻ nhất/tháng",
        desc: "Dành cho doanh nghiệp cam kết dài hạn, tiết kiệm tối đa.",
        features: [
          { text: "Tất cả tính năng gói 6 tháng" },
          { text: "Training online 1:1", hl: true },
          { text: "Hỗ trợ trong 48h", hl: true },
          { text: "Loại bỏ \"Bombot.vn\" trong tin nhắn" },
        ],
      },
    ],
    lifetime: [
      {
        id: "2648",
        name: "Vĩnh viễn",
        discount: "-80%",
        price: "5.499K",
        original: "19.999K",
        priceNote: "Trả 1 lần, dùng mãi mãi",
        desc: "Mua một lần, dùng mãi mãi. Không lo gia hạn, không phí phát sinh.",
        features: [
          { text: "Tất cả tính năng gói tháng", hl: true },
          { text: "Sử dụng trọn đời" },
          { text: "Training online 1:1" },
          { text: "Hỗ trợ nhanh trong 48h" },
          { text: "Loại bỏ \"Bombot.vn\" trong tin nhắn" },
        ],
      },
      {
        id: "2651",
        name: "Vô hạn",
        discount: "-84%",
        price: "6.499K",
        original: "29.999K",
        priceNote: "Chỉ +1.000K so với gói Vĩnh viễn",
        desc: "Tất cả tính năng không giới hạn, hỗ trợ ưu tiên và cập nhật miễn phí mãi mãi.",
        popular: true,
        features: [
          { text: "Tất cả tính năng Vĩnh viễn" },
          { text: "Không giới hạn nâng cấp" },
          { text: "Training online 1:1" },
          { text: "Hỗ trợ ưu tiên trong 24h", hl: true },
          { text: "Loại bỏ \"Bombot.vn\" trong tin nhắn" },
        ],
      },
    ],
    socialProof: [
      { icon: "check", text: "<b>4.800+</b> doanh nghiệp đang dùng" },
      { icon: "stars", text: "<b>4.8/5</b> đánh giá" },
      { icon: "shield", text: "Bảo mật thanh toán chuẩn PCI-DSS" },
    ],
  },
  en: {
    chipLabel: "Current plan: Free",
    title: "Upgrade for unlimited messaging",
    subtitle: "Choose your plan — cancel anytime, no hidden fees.",
    monthlyTab: "Monthly",
    lifetimeTab: "Lifetime",
    lifetimeSave: "save up to 84%",
    popularBadge: "Most popular",
    ctaDefault: "Get started",
    ctaPopular: "Get started →",
    upsellText: "Only <b>+$360</b> to upgrade from <b>Eternity</b> to <b>Infinite</b> — unlock all future upgrades, free for life.",
    monthly: [
      {
        id: "2638",
        name: "3 months",
        discount: "-23%",
        price: "$48",
        original: "$73",
        priceNote: "≈ $16/month",
        desc: "Perfect for businesses wanting to test before a long-term commitment.",
        features: [
          { text: "Unlimited messages" },
          { text: "Unlimited Fanpages" },
          { text: "Phone filtering + tagging" },
          { text: "Support within 72h" },
          { text: 'Remove "Bombot.vn" from messages', hl: true },
        ],
      },
      {
        id: "4416",
        name: "6 months",
        discount: "-21%",
        price: "$89",
        original: "$146",
        priceNote: "≈ $15/month — cheaper than 3-month",
        desc: "Optimal choice for growing businesses.",
        popular: true,
        features: [
          { text: "All 3-month plan features" },
          { text: "Priority send queue", hl: true },
          { text: "Support within 72h" },
          { text: 'Remove "Bombot.vn" from messages' },
        ],
      },
      {
        id: "2639",
        name: "12 months",
        discount: "-25%",
        price: "$144",
        original: "$208",
        priceNote: "≈ $12/month — lowest per month",
        desc: "For businesses committed long-term, maximum savings.",
        features: [
          { text: "All 6-month plan features" },
          { text: "1:1 online training", hl: true },
          { text: "Support within 48h", hl: true },
          { text: 'Remove "Bombot.vn" from messages' },
        ],
      },
    ],
    lifetime: [
      {
        id: "2649",
        name: "Eternity",
        discount: "-80%",
        price: "$540",
        original: "$869",
        priceNote: "Pay once, use forever",
        desc: "Buy once, use forever. No renewals, no extra fees.",
        features: [
          { text: "All monthly plan features", hl: true },
          { text: "Lifetime use" },
          { text: "1:1 online training" },
          { text: "Fast support within 48h" },
          { text: 'Remove "Bombot.vn" from messages' },
        ],
      },
      {
        id: "2652",
        name: "Infinite",
        discount: "-84%",
        price: "$900",
        original: "$1,300",
        priceNote: "Only +$360 vs Eternity",
        desc: "All features unlimited, priority support and free updates forever.",
        popular: true,
        features: [
          { text: "All Eternity features" },
          { text: "Unlimited upgrades" },
          { text: "1:1 online training" },
          { text: "Priority support within 24h", hl: true },
          { text: 'Remove "Bombot.vn" from messages' },
        ],
      },
    ],
    socialProof: [
      { icon: "check", text: "<b>4,800+</b> businesses using" },
      { icon: "stars", text: "<b>4.8/5</b> rating" },
      { icon: "shield", text: "PCI-DSS secure payments" },
    ],
  },
};

/* ─── SVG helpers ───────────────────────────────────────────────────────── */
const CheckSvg = () => (
  <svg viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
)
const StarSvg = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2H22l-6 4.4 2.4 7.2L12 16.4 5.6 20.8 8 13.6 2 9.2h7.6z"/></svg>
)
const ShieldSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4.5 8-11V5l-8-3-8 3v6c0 6.5 8 11 8 11z"/></svg>
)
const BoltSvg = () => (
  <svg viewBox="0 0 24 24" fill="#F59E0B"><path d="M13 2L3 14h6l-1 8 10-12h-6l1-8z"/></svg>
)

/* ─── Component ─────────────────────────────────────────────────────────── */
const PricingModal = ({ open, onClose, user, i18n }) => {
  const cls = useStyles();
  const [tab, setTab] = useState("monthly");

  const langKey = i18n?.language?.startsWith("vi") ? "vi" : "en";
  const data = pricingData[langKey];
  const plans = data[tab];

  // Lấy tên gói từ getWhatYouNeed API (share.package.name) trong Redux store
  const share = useSelector(selectShare);
  const currentPlanName = share?.package?.name
    || user?.package_name
    || (langKey === "vi" ? "Dùng thử" : "Free Trial");
  const chipLabel = langKey === "vi"
    ? `Gói hiện tại: ${currentPlanName}`
    : `Current plan: ${currentPlanName}`;

  const handleCheckout = (productId) => {
    const url = buildCheckoutUrl({ lang: langKey, productId, user });
    window.open(url, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: cls.paper }}
    >
      {/* Close */}
      <button className={cls.closeBtn} onClick={onClose} aria-label="Đóng">
        <svg viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/></svg>
      </button>

      {/* Header */}
      <div className={cls.header}>
        <div className={cls.chipBadge}>
          <StarSvg />
          {chipLabel}
        </div>
        <h2 className={cls.title}>{data.title}</h2>
        <p className={cls.subtitle}>{data.subtitle}</p>
      </div>

      {/* Toggle */}
      <div className={cls.toggleWrap}>
        <div className={cls.toggleInner}>
          <button
            className={`${cls.tabBtn} ${tab === "monthly" ? cls.tabActive : ""}`}
            onClick={() => setTab("monthly")}
          >
            {data.monthlyTab}
          </button>
          <button
            className={`${cls.tabBtn} ${tab === "lifetime" ? cls.tabActive : ""}`}
            onClick={() => setTab("lifetime")}
          >
            {data.lifetimeTab}
            <span className={cls.lifetimeSaveBadge}>{data.lifetimeSave}</span>
          </button>
        </div>
      </div>

      {/* Plan cards */}
      <div className={`${cls.grid} ${tab === "lifetime" ? cls.grid2 : ""}`}>
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`${cls.card} ${plan.popular ? cls.cardPopular : ""}`}
          >
            {plan.popular && (
              <div className={cls.popularBadge}>
                <StarSvg />
                {data.popularBadge}
              </div>
            )}

            <div className={cls.cardHead}>
              <span className={cls.planName}>{plan.name}</span>
              <span className={cls.discountChip}>{plan.discount}</span>
            </div>

            <div className={cls.priceRow}>
              <span className={cls.priceMain}>{plan.price}</span>
              <span className={cls.priceOrig}>{plan.original}</span>
            </div>
            <div className={`${cls.priceNote} ${plan.popular ? cls.priceNotePopular : ""}`}>
              {plan.priceNote}
            </div>

            <div className={cls.planDesc}>{plan.desc}</div>

            <button
              className={`${cls.ctaBtn} ${plan.popular ? cls.ctaBtnPopular : ""}`}
              onClick={() => handleCheckout(plan.id)}
            >
              {plan.popular ? data.ctaPopular : data.ctaDefault}
            </button>

            <div className={`${cls.featureList} ${plan.popular ? cls.featureListPopular : ""}`}>
              {plan.features.map((f, i) => (
                <div
                  key={i}
                  className={`${cls.featItem} ${plan.popular ? cls.featItemPopular : ""} ${f.hl ? cls.featHl : ""}`}
                >
                  <CheckSvg />
                  {f.text}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Upsell strip (lifetime only) */}
      {tab === "lifetime" && (
        <div className={cls.upsellStrip}>
          <BoltSvg />
          <span dangerouslySetInnerHTML={{ __html: data.upsellText }} />
        </div>
      )}

      {/* Social proof */}
      <div className={cls.socialProof}>
        {data.socialProof.map((s, i) => (
          <div key={i} className={cls.spItem}>
            {s.icon === "check" && (
              <svg viewBox="0 0 24 24" fill={T.brand}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            )}
            {s.icon === "stars" && (
              <span className={cls.stars}>★★★★★</span>
            )}
            {s.icon === "shield" && (
              <svg viewBox="0 0 24 24" fill="none" stroke={T.brand} strokeWidth="2" style={{width:14,height:14,flexShrink:0}}>
                <path d="M12 22s8-4.5 8-11V5l-8-3-8 3v6c0 6.5 8 11 8 11z"/>
              </svg>
            )}
            <span dangerouslySetInnerHTML={{ __html: s.text }} />
          </div>
        ))}
      </div>
    </Dialog>
  );
};

export default PricingModal;
